import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api";

function getUpdateVersionErrorMessage(error) {
  const status = error.response?.status;
  const data = error.response?.data ?? {};

  switch (status) {
    case 401:
      return data.message || "Authorization required. Please sign in again.";
    case 403:
      return data.message || "Access denied. Your role cannot update the app version.";
    case 404:
      return data.error || "No version record found.";
    case 500:
      return data.error || "Server error. Please try again later.";
    default:
      return (
        data.message ||
        data.error ||
        "Error updating version. Please try again."
      );
  }
}

const UpdateVersionButton = () => {
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const handleVersionChange = (e) => {
    setVersion(e.target.value);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleUpdateVersion = async () => {
    const trimmedVersion = version.trim();
    if (!trimmedVersion) {
      setErrorMessage("Please enter a version number.");
      return;
    }

    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const response = await axios.put("/update-versions", {
        version: trimmedVersion,
      });

      setSuccessMessage(`Version updated to ${response.data.version} successfully!`);
      setVersion("");
    } catch (error) {
      console.error("Error updating version:", error);
      setErrorMessage(getUpdateVersionErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: "24px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 56, marginBottom: 32 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Update App Version
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Enter the new version number below to update the app
        </p>
      </div>

      <div
        style={{
          maxWidth: 420,
          margin: "0 auto",
        }}
      >
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#64748b",
          }}
        >
          Version number
        </label>
        <input
          type="text"
          placeholder="e.g. 2.1.0"
          value={version}
          onChange={handleVersionChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loading && version.trim()) {
              handleUpdateVersion();
            }
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "0.75rem 1rem",
            fontSize: "1rem",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            outline: "none",
            marginBottom: 16,
            transition: "border-color 0.2s, box-shadow 0.2s",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#667eea";
            e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.15)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#e2e8f0";
            e.target.style.boxShadow = "none";
          }}
        />

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <CircularProgress size={28} sx={{ color: "#667eea" }} />
          </div>
        )}

        {successMessage && (
          <p
            style={{
              margin: "0 0 16px",
              color: "#16a34a",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            {successMessage}
          </p>
        )}
        {errorMessage && (
          <p
            style={{
              margin: "0 0 16px",
              color: "#dc2626",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            {errorMessage}
          </p>
        )}

        <button
          onClick={handleUpdateVersion}
          disabled={loading || !version.trim()}
          style={{
            width: "100%",
            padding: "0.75rem 1rem",
            fontSize: "1rem",
            fontWeight: 600,
            background:
              loading || !version.trim()
                ? "#94a3b8"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: loading || !version.trim() ? "not-allowed" : "pointer",
            boxShadow:
              loading || !version.trim()
                ? "none"
                : "0 2px 8px rgba(102, 126, 234, 0.3)",
            transition: "opacity 0.2s",
          }}
        >
          Update
        </button>
      </div>
    </div>
  );
};

export default UpdateVersionButton;
